import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editFlowProperties } from 'store/units/sagaActions';
import { getPorts } from 'store/units/selectors';

import type {
  FlowPropertiesInterface,
  OutputFlowInterface,
  OutputFlowMapInterface,
  PortInterfaceWithName,
  UnitInterface,
} from 'store/units/types';
import { DisplayGraphMode, PortMode } from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import { getPxxValues } from './util';

const withCalculatedPxxValues = (
  flowProperties: FlowPropertiesInterface,
  outputPorts: PortInterfaceWithName[],
  currentPort: PortInterfaceWithName | null,
) => {
  const outputs: OutputFlowMapInterface = {};
  outputPorts?.forEach((port) => {
    if (port.key === currentPort?.key) {
      const newOutput: OutputFlowInterface = {
        pxxValues: getPxxValues(
          flowProperties.pxxOne,
          flowProperties.pxxTwo,
          flowProperties.pxxThree,
          currentPort?.outputFlow,
          flowProperties.psdCalcMode,
        ),
      };
      Object.assign(outputs, { [port.key]: newOutput });
    } else {
      /* Calc pxx values for other ports than the active one */
      const otherOutput: OutputFlowInterface = {
        pxxValues: getPxxValues(
          flowProperties.pxxOne,
          flowProperties.pxxTwo,
          flowProperties.pxxThree,
          port?.outputFlow,
          flowProperties.psdCalcMode,
        ),
      };
      Object.assign(outputs, { [port.key]: otherOutput });
    }
  });
  return {
    ...flowProperties,
    outputs: outputs,
  };
};

const defaultPxxOne = 50;
const defaultPxxTwo = 80;
const defaultPxxThree = 90;

export const useEditFlowProperties = <T extends FlowPropertiesInterface>(
  unitId: string,
  activeUnit: UnitInterface,
  flowProperties: T,
) => {
  const dispatch = useDispatch();
  const ports = useSelector(getPorts);
  const [outputPorts, setOutputPorts] = useState<PortInterfaceWithName[]>([]);
  const [inputPorts, setInputPorts] = useState<PortInterfaceWithName[]>([]);

  const [currentPort, setCurrentPort] = useState<PortInterfaceWithName | null>(
    null,
  );
  const [isStorage, setIsStorage] = useState(false);

  const setValue = <U extends keyof T>(
    key: U | [U, keyof T[U]],
    value: T[U] | T[U][keyof T[U]],
  ) => {
    if (Array.isArray(key)) {
      const newFlowProperties = {
        ...flowProperties,
        [key[0]]: { ...flowProperties[key[0]], [key[1]]: value },
      };
      dispatch(
        editFlowProperties(
          unitId,
          withCalculatedPxxValues(newFlowProperties, outputPorts, currentPort),
        ),
      );
    } else {
      if (flowProperties[key] !== value) {
        const newUnitInfo = {
          ...flowProperties,
          [key]: value,
        };
        dispatch(editFlowProperties(unitId, newUnitInfo));
      }
    }
  };

  const setValues = (values: Partial<T>) => {
    dispatch(editFlowProperties(unitId, { ...flowProperties, ...values }));
  };

  useEffect(() => {
    const unitOutputPorts = ports[unitId]
      ?.filter(
        (p) =>
          p.portMode === PortMode.OUTPUT &&
          p.portIndex <= activeUnit.layout.outputPorts,
      )
      .map((p) => {
        if (activeUnit.type === UnitTypes.ADD) {
          return {
            name: `Output 1`, // Since portIndex is 0 for ADD
            ...p,
          };
        }
        return {
          name: `Output ${p.portIndex}`,
          ...p,
        };
      });
    const unitInputPorts: PortInterfaceWithName[] = ports[unitId]
      ?.filter((p) => p.portMode === PortMode.INPUT)
      .map((p) => {
        return {
          name: `Input ${p.portIndex + 1}`,
          ...p,
        };
      });

    setOutputPorts(unitOutputPorts);
    setInputPorts(unitInputPorts);

    if (unitOutputPorts.length > 0) {
      setCurrentPort(unitOutputPorts[flowProperties.activePortIndex]);
    } else if (unitInputPorts.length > 0) {
      setCurrentPort(unitInputPorts[0]);
      setIsStorage(true);
      setValue('graphMode', DisplayGraphMode.FEED_AND_DISCHARGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, ports, flowProperties.activePortIndex]);

  useEffect(() => {
    const { pxxOne, pxxTwo, pxxThree } = flowProperties;

    if ([pxxOne, pxxTwo, pxxThree].some((s) => s === undefined)) {
      setValues({
        ...flowProperties,
        pxxOne: pxxOne ?? defaultPxxOne,
        pxxTwo: pxxTwo ?? defaultPxxTwo,
        pxxThree: pxxThree ?? defaultPxxThree,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  return {
    setValue,
    setValues,
    outputPorts,
    inputPorts,
    currentPort,
    isStorage,
  };
};
